import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Coins, FileDown, Plus, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { getMyCreditState, orderCreditPack } from "@/lib/api/credits.functions";
import { PRICE_PER_CREDIT_ZAR, ADMIN_FEE_ZAR, MIN_CREDIT_SPEND_ZAR } from "@/lib/credit-packs";
import { computeCreditBreakdown } from "@/lib/credit-rounding";
import { CreditBreakdownPanel } from "@/components/site/CreditBreakdownPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/portal/credits")({
  component: MyCreditsPage,
});

function MyCreditsPage() {
  const fetchState = useServerFn(getMyCreditState);
  const orderPack = useServerFn(orderCreditPack);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [amountInput, setAmountInput] = useState<string>(String(MIN_CREDIT_SPEND_ZAR + ADMIN_FEE_ZAR));
  const [topUpBusy, setTopUpBusy] = useState(false);
  const [viewerEmail, setViewerEmail] = useState<string>("");
  useEffect(() => {
    supabase.auth.getUser().then((r) => setViewerEmail(r.data.user?.email ?? ""));
  }, []);


  const rawAmount = Number(amountInput);
  const breakdown = useMemo(() => computeCreditBreakdown(rawAmount), [rawAmount]);
  const minCredits = breakdown.minCredits;
  const creditsForAmount = breakdown.credits;
  const totalCharge = breakdown.totalZar;
  const meetsMin = breakdown.meetsMinimum;

  async function startCustomTopUp() {
    if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
      toast.error("Enter a valid amount in Rand.");
      return;
    }
    if (!meetsMin) {
      toast.error(`Minimum top-up is R${MIN_CREDIT_SPEND_ZAR} (${minCredits} credits). A R${ADMIN_FEE_ZAR} admin fee will be added.`);
      return;
    }
    setTopUpBusy(true);
    try {
      const r = await orderPack({ data: { customCredits: creditsForAmount } });
      toast.success(`Redirecting to PayFast — total R${totalCharge.toFixed(2)} (includes R${ADMIN_FEE_ZAR} admin fee).`);
      const { launchPayfastCheckout } = await import("@/lib/payfast-launch");
      await launchPayfastCheckout(r.reference);
    } catch (e: any) {
      toast.error("Could not create top-up", { description: e?.message ?? String(e) });
    } finally {
      setTopUpBusy(false);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ["my-credits"],
    queryFn: () => fetchState(),
  });

  // Real-time PayFast payment status: any UPDATE to my credit_orders rows
  // triggers a refresh so "Awaiting EFT" flips to "Paid" without reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid || cancelled) return;
      const channel = supabase
        .channel(`my-credit-orders-${uid}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "credit_orders",
            filter: `user_id=eq.${uid}`,
          },
          (payload) => {
            qc.invalidateQueries({ queryKey: ["my-credits"] });
            const newRow = (payload as any).new;
            const oldRow = (payload as any).old;
            if (newRow?.status === "paid" && oldRow?.status !== "paid") {
              toast.success(
                `Payment received via PayFast — ${newRow.credits} credits added (R${Number(newRow.amount_zar).toFixed(2)} total).`,
              );
              // also refresh any open invoice query for this ref
              if (newRow.reference) {
                qc.invalidateQueries({ queryKey: ["invoice", newRow.reference] });
              }
            }
          },
        )
        .subscribe();
      return () => {
        supabase.removeChannel(channel);
      };
    })();
    return () => {
      cancelled = true;
    };
  }, [qc]);


  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const balance = data?.balance ?? 0;
  const ledger = data?.ledger ?? [];
  const orders = data?.orders ?? [];

  return (
    <div className="space-y-6">
      <section className="flex items-center justify-between rounded-2xl border border-gold/40 bg-secondary/30 p-6">
        <div className="flex items-center gap-3">
          <Coins className="h-8 w-8 text-gold" />
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Balance</p>
            <p className="font-display text-3xl text-navy">{balance} <span className="text-base font-normal text-muted-foreground">credits</span></p>
          </div>
        </div>
        <Button asChild className="bg-navy hover:bg-navy/90">
          <Link to={"/credits" as any}><Plus className="mr-1 h-4 w-4" /> Top up</Link>
        </Button>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-gold" />
          <h2 className="font-display text-lg text-navy">Custom top-up via PayFast</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Enter any amount from <strong>R{MIN_CREDIT_SPEND_ZAR}</strong> upwards.
          Each credit costs <strong>R{PRICE_PER_CREDIT_ZAR.toFixed(2)}</strong>.
          A <strong>R{ADMIN_FEE_ZAR}</strong> admin fee is added per top-up.
          Payment is processed securely by PayFast.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <label className="block">
            <span className="text-xs font-semibold text-navy">Credit amount (ZAR)</span>
            <div className="mt-1 flex items-center rounded-md border border-input bg-background px-3 focus-within:border-navy">
              <span className="text-sm text-muted-foreground">R</span>
              <Input
                type="number"
                min={MIN_CREDIT_SPEND_ZAR}
                step={1}
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                className="border-0 bg-transparent focus-visible:ring-0"
                placeholder={String(MIN_CREDIT_SPEND_ZAR)}
              />
            </div>
          </label>
          <Button
            onClick={startCustomTopUp}
            disabled={topUpBusy || !meetsMin}
            className="bg-navy hover:bg-navy/90"
          >
            {topUpBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
            Pay R{totalCharge.toFixed(2)} with PayFast
          </Button>
        </div>

        <div className="mt-3">
          <CreditBreakdownPanel breakdown={breakdown} />
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Prefer a preset bundle? <Link to={"/credits" as any} className="font-semibold text-navy underline">Browse credit packs</Link>.
        </p>
      </section>


      <section>
        <h2 className="mb-3 font-display text-lg text-navy">Recent activity</h2>
        {ledger.length === 0 ? (
          <p className="text-sm text-muted-foreground">No activity yet.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Ref</th><th className="px-3 py-2 text-right">Δ</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {ledger.map((l: any, i: number) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-xs">{new Date(l.created_at).toLocaleString("en-ZA")}</td>
                    <td className="px-3 py-2">{l.reason}</td>
                    <td className="px-3 py-2 font-mono text-xs">{l.ref ?? "—"}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${l.delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <InvoiceHistorySection orders={orders} viewerEmail={viewerEmail} />
    </div>
  );
}

type StatusFilter = "all" | "paid" | "pending" | "cancelled" | "failed";
const PAGE_SIZE = 10;

function InvoiceHistorySection({ orders, viewerEmail }: { orders: any[]; viewerEmail: string }) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const fromTs = fromDate ? new Date(fromDate).getTime() : null;
    const toTs = toDate ? new Date(toDate + "T23:59:59").getTime() : null;
    return orders.filter((o: any) => {
      if (statusFilter !== "all") {
        const s = o.status === "paid" ? "paid"
          : o.status === "cancelled" ? "cancelled"
          : o.status === "failed" ? "failed"
          : "pending";
        if (s !== statusFilter) return false;
      }
      const t = new Date(o.created_at).getTime();
      if (fromTs && t < fromTs) return false;
      if (toTs && t > toTs) return false;
      return true;
    });
  }, [orders, statusFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset to first page whenever filters change.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, fromDate, toDate]);

  const filterBtn = (key: StatusFilter, label: string, count?: number) => (
    <button
      key={key}
      onClick={() => setStatusFilter(key)}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
        statusFilter === key
          ? "border-navy bg-navy text-white"
          : "border-border bg-background text-muted-foreground hover:border-navy/40"
      }`}
    >
      {label}
      {typeof count === "number" && (
        <span className="ml-1 opacity-70">({count})</span>
      )}
    </button>
  );

  const counts = useMemo(() => {
    const base = { all: orders.length, paid: 0, pending: 0, cancelled: 0, failed: 0 };
    for (const o of orders) {
      if (o.status === "paid") base.paid++;
      else if (o.status === "cancelled") base.cancelled++;
      else if (o.status === "failed") base.failed++;
      else base.pending++;
    }
    return base;
  }, [orders]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg text-navy">Top-up invoice history</h2>
        <span className="text-[11px] text-muted-foreground">
          Updates live · {filtered.length} of {orders.length} invoices
        </span>
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
        <div className="flex flex-wrap gap-1.5">
          {filterBtn("all", "All", counts.all)}
          {filterBtn("paid", "Paid", counts.paid)}
          {filterBtn("pending", "Pending", counts.pending)}
          {filterBtn("failed", "Failed", counts.failed)}
          {filterBtn("cancelled", "Cancelled", counts.cancelled)}
        </div>
        <div className="ml-auto flex flex-wrap items-end gap-2 text-xs">
          <label className="block">
            <span className="block text-muted-foreground">From</span>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-8 w-[150px]"
            />
          </label>
          <label className="block">
            <span className="block text-muted-foreground">To</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-8 w-[150px]"
            />
          </label>
          {(fromDate || toDate || statusFilter !== "all") && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => {
                setStatusFilter("all");
                setFromDate("");
                setToDate("");
              }}
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No invoices match these filters.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Credits</th>
                  <th className="px-3 py-2 text-right">Credits value</th>
                  <th className="px-3 py-2 text-right">Admin fee</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Paid</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pageRows.map((o: any) => {
                  const paid = o.status === "paid";
                  const isPayfast = o.payment_method === "payfast";
                  const total = Number(o.amount_zar) || 0;
                  const creditsValue = Math.max(0, +(total - ADMIN_FEE_ZAR).toFixed(2));
                  const adminFee = total > 0 ? ADMIN_FEE_ZAR : 0;
                  return (
                    <tr key={o.id} className="hover:bg-muted/40">
                      <td className="px-3 py-2">
                        <Link
                          to={"/invoice/$ref" as any}
                          params={{ ref: o.reference } as any}
                          className="font-mono text-xs text-navy hover:underline"
                        >
                          {o.reference}
                        </Link>
                      </td>
                      <td className="px-3 py-2">{o.credits}</td>
                      <td className="px-3 py-2 text-right">R{creditsValue.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        R{adminFee.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">R{total.toFixed(2)}</td>
                      <td className="px-3 py-2">
                        {paid ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                            {isPayfast ? "Paid via PayFast" : "Paid"}
                          </span>
                        ) : o.status === "cancelled" ? (
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                            Cancelled
                          </span>
                        ) : o.status === "failed" ? (
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                            Failed
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(o.created_at).toLocaleString("en-ZA")}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {paid && o.paid_at ? new Date(o.paid_at).toLocaleString("en-ZA") : "—"}
                        {paid && o.paid_provider_ref && (
                          <span className="block font-mono text-[10px]">
                            ref {o.paid_provider_ref}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-1">
                          <a
                            href={`/api/public/receipt?ref=${encodeURIComponent(o.reference)}${(o as any).public_token ? `&t=${encodeURIComponent((o as any).public_token)}` : ""}&doc=invoice`}
                            className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-[11px] hover:border-navy"
                            title="Download invoice PDF"
                          >
                            <FileDown className="h-3 w-3" /> Invoice
                          </a>
                          {paid ? (
                            <a
                              href={`/api/public/receipt?ref=${encodeURIComponent(o.reference)}${(o as any).public_token ? `&t=${encodeURIComponent((o as any).public_token)}` : ""}`}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800 hover:border-emerald-500"
                              title="Download branded PDF receipt"
                            >
                              <FileDown className="h-3 w-3" /> Receipt
                            </a>



                          ) : (
                            <Link
                              to={"/invoice/$ref" as any}
                              params={{ ref: o.reference } as any}
                              className="inline-flex items-center gap-1 rounded-md bg-navy px-2 py-1 text-[11px] font-semibold text-white hover:bg-navy/90"
                            >
                              Pay now
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Page {safePage} of {totalPages} · {PAGE_SIZE} per page
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
